import {
  cleanScore,
  onTimePoints,
  storeGrade,
  TIMELINESS_WEIGHTS,
  type StoreGrade,
} from './grades';
import type { Party } from './requirements';
import type { WindowStatus } from './windows';

/**
 * "Why this grade?" (owner ask 2026-10-18; grade split in two 2026-10-19,
 * docs/dashboards.md): the same numbers as storeGrade(), explained.
 *
 * TIMELINESS as POINTS LOST per cause — late audits (which clock, whose)
 * and overdue tasks (which, whose):
 *
 *   points lost by a part = its weight × (100 − part) / 100
 *
 * Weights are 60 / 40, so the two add up to exactly 100 − timeliness.
 * Each part's loss is split over the facts behind it: per started clock
 * (the on-time part averages them), per open task (each an equal share of
 * the task part). Clocks not started yet lose nothing.
 *
 * QUALITY as the audits it averages (up to the last 3 scored ones).
 */

export type Cause = 'onTime' | 'tasks';
export const CAUSES: readonly Cause[] = ['onTime', 'tasks'];
/** The two timeliness parts, as the grade names them. */
export const CAUSE_LABEL: Record<Cause, string> = {
  onTime: 'Audits on time',
  tasks: 'Open tasks',
};
/** What a part losing points means, for "Mostly …" sentences. */
export const CAUSE_DRAG: Record<Cause, string> = {
  onTime: 'audits not done on time',
  tasks: 'overdue tasks',
};

export interface ClockInput<T = unknown> {
  status: WindowStatus;
  daysOverdue: number;
  party: Party;
  /** Carried through untouched (template, dates…) for the UI. */
  ref: T;
}

export interface TaskInput<T = unknown> {
  overdue: boolean;
  ref: T;
}

export interface AuditInput<T = unknown> {
  /** null = no score (all N/A, or a score-less form) — never counted. */
  score: number | null;
  ref: T;
}

export interface ClockFact<T = unknown> extends ClockInput<T> {
  /** On-time points for this clock; null = not started (no effect). */
  points: number | null;
  /** Timeliness points this clock costs (0 when current or not started). */
  lost: number;
}

export interface TaskFact<T = unknown> extends TaskInput<T> {
  lost: number;
}

export interface StoreBreakdown<C = unknown, K = unknown, A = unknown> {
  grade: StoreGrade;
  /** Weight of each timeliness part (sums to 100; 0s when ungraded). */
  weights: Record<Cause, number>;
  /** Exact timeliness points lost per part; sum = 100 − timeliness (0s when ungraded). */
  lost: Record<Cause, number>;
  clocks: ClockFact<C>[];
  /** Every open task, with what it costs (0 = on time). */
  tasks: TaskFact<K>[];
  /** Timeliness part costing the most; null when nothing costs a displayed point. */
  biggest: Cause | null;
  /** The audits averaged into quality, newest first (their scores = grade.scores). */
  qualityAudits: { score: number; ref: A }[];
}

const ZERO: Record<Cause, number> = { onTime: 0, tasks: 0 };

export function storeBreakdown<C, K, A>(input: {
  clocks: readonly ClockInput<C>[];
  tasks: readonly TaskInput<K>[];
  /** The store's DM/Auditor audits, NEWEST FIRST. */
  audits: readonly AuditInput<A>[];
}): StoreBreakdown<C, K, A> {
  const grade = storeGrade({
    windows: input.clocks.map((c) => ({ status: c.status, daysOverdue: c.daysOverdue })),
    tasks: input.tasks,
    recentScores: input.audits.map((a) => a.score),
  });
  // The same audits storeGrade averaged: the first N usable scores.
  const counted: { score: number; ref: A }[] = [];
  for (const a of input.audits) {
    if (counted.length === grade.scores.length) break;
    const score = grade.scores[counted.length];
    if (cleanScore(a.score) !== null && score !== undefined) counted.push({ score, ref: a.ref });
  }
  const withPoints = input.clocks.map((c) => ({ ...c, points: onTimePoints(c.status, c.daysOverdue) }));
  if (grade.timeliness === null) {
    return {
      grade,
      weights: { ...ZERO },
      lost: { ...ZERO },
      clocks: withPoints.map((c) => ({ ...c, lost: 0 })),
      tasks: input.tasks.map((t) => ({ ...t, lost: 0 })),
      biggest: null,
      qualityAudits: counted,
    };
  }
  const weights: Record<Cause, number> = { onTime: TIMELINESS_WEIGHTS.onTime, tasks: TIMELINESS_WEIGHTS.tasks };
  const started = withPoints.filter((c) => c.points !== null).length;
  const clocks = withPoints.map((c) => ({
    ...c,
    lost: c.points === null ? 0 : (weights.onTime * (100 - c.points)) / 100 / started,
  }));
  const n = input.tasks.length;
  const tasks = input.tasks.map((t) => {
    return { ...t, lost: t.overdue ? weights.tasks / n : 0 };
  });
  const lost: Record<Cause, number> = {
    onTime: (weights.onTime * (100 - (grade.onTime ?? 100))) / 100,
    tasks: (weights.tasks * (100 - grade.tasks)) / 100,
  };
  return { grade, weights, lost, clocks, tasks, biggest: biggestCause(lost), qualityAudits: counted };
}

/** The part costing the most (ties: on time, then tasks); null below half a point. */
export function biggestCause(lost: Record<Cause, number>): Cause | null {
  let best: Cause | null = null;
  for (const c of CAUSES) if (lost[c] >= 0.5 && (best === null || lost[c] > lost[best])) best = c;
  return best;
}

/**
 * Whole numbers that add up to `target` (largest remainder), so the points
 * lost shown in a popover always add up to 100 minus the grade on the chip.
 */
export function roundToTotal(values: readonly number[], target: number): number[] {
  const floors = values.map((v) => Math.floor(Math.max(0, v)));
  let left = target - floors.reduce((a, b) => a + b, 0);
  const order = values
    .map((v, i) => ({ i, frac: Math.max(0, v) - floors[i]! }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  const out = [...floors];
  for (const { i } of order) {
    if (left <= 0) break;
    out[i] = out[i]! + 1;
    left -= 1;
  }
  return out;
}

/** Timeliness points lost per part as displayed: whole numbers summing to 100 − the shown grade. */
export function shownLost(lost: Record<Cause, number>, grade: number): Record<Cause, number> {
  const [onTime, tasks] = roundToTotal(
    CAUSES.map((c) => lost[c]),
    100 - Math.round(grade),
  );
  return { onTime: onTime!, tasks: tasks! };
}

/** Who the timeliness points went to (owner ruling 2026-10-28: "is it the DM or the Auditor?"). */
export type LostTo = 'dm' | 'auditor' | 'store' | 'tasks';
export const LOST_TO: readonly LostTo[] = ['dm', 'auditor', 'store', 'tasks'];
export const LOST_TO_LABEL: Record<LostTo, string> = {
  dm: 'DM audits',
  auditor: 'Auditor audits',
  store: 'Store-team audits',
  tasks: 'Store’s tasks',
};

/**
 * Timeliness points lost, split by WHO: each late clock's loss goes to its
 * party (DM / Auditor / store team), overdue tasks to the store. Whole
 * numbers built FROM shownLost — the store's tasks are exactly the bars'
 * "Open tasks", and DM + Auditor + store team add up to the bars' "Audits
 * on time" (QA 2026-10-28: rounding four parts alone could disagree).
 */
export function lostByWho(b: Pick<StoreBreakdown, 'grade' | 'clocks' | 'lost'>): Record<LostTo, number> {
  if (b.grade.timeliness === null) return { dm: 0, auditor: 0, store: 0, tasks: 0 };
  const shown = shownLost(b.lost, b.grade.timeliness);
  const exact = { dm: 0, auditor: 0, store: 0 };
  for (const c of b.clocks) exact[c.party] += c.lost;
  const [dm, auditor, store] = roundToTotal([exact.dm, exact.auditor, exact.store], shown.onTime);
  return { dm: dm!, auditor: auditor!, store: store!, tasks: shown.tasks };
}

export interface StorePull<S> {
  store: S;
  grade: number;
  /** Points this store takes off the district average. */
  pull: number;
  /** Timeliness only: the store's biggest cause. */
  biggest: Cause | null;
}

export interface DistrictBreakdown<S> {
  timeliness: {
    /** Average of the graded stores; null = none graded. */
    grade: number | null;
    graded: number;
    /** Average points lost per part across graded stores; sum = 100 − grade. */
    lost: Record<Cause, number>;
    weights: Record<Cause, number>;
    biggest: Cause | null;
    /** Graded stores, biggest pull first (ties: input order). */
    pulls: StorePull<S>[];
  };
  quality: {
    /** Average of the stores' quality grades; null = no scored audit anywhere. */
    grade: number | null;
    graded: number;
    /** Stores with a quality grade, biggest pull (lowest quality) first. */
    pulls: StorePull<S>[];
  };
}

function pullsOf<S>(graded: readonly { store: S; grade: number; biggest: Cause | null }[]): StorePull<S>[] {
  const n = graded.length;
  return graded
    .map((s, i) => ({ i, store: s.store, grade: s.grade, pull: (100 - s.grade) / n, biggest: s.biggest }))
    .sort((a, b) => b.pull - a.pull || a.i - b.i)
    .map(({ i: _i, ...p }) => p);
}

/** District = average of its graded stores, so each costs (100 − its grade) ÷ graded stores — per grade. */
export function districtBreakdown<S>(
  stores: readonly { store: S; breakdown: StoreBreakdown }[],
): DistrictBreakdown<S> {
  const timed = stores.filter((s) => s.breakdown.grade.timeliness !== null);
  const n = timed.length;
  const lost: Record<Cause, number> = { ...ZERO };
  const weights: Record<Cause, number> = { ...ZERO };
  for (const s of timed) {
    for (const c of CAUSES) {
      lost[c] += s.breakdown.lost[c] / n;
      weights[c] += s.breakdown.weights[c] / n;
    }
  }
  const scored = stores.filter((s) => s.breakdown.grade.quality !== null);
  const avg = (xs: readonly number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
  return {
    timeliness: {
      grade: avg(timed.map((s) => s.breakdown.grade.timeliness!)),
      graded: n,
      lost,
      weights,
      biggest: n ? biggestCause(lost) : null,
      pulls: pullsOf(timed.map((s) => ({ store: s.store, grade: s.breakdown.grade.timeliness!, biggest: s.breakdown.biggest }))),
    },
    quality: {
      grade: avg(scored.map((s) => s.breakdown.grade.quality!)),
      graded: scored.length,
      pulls: pullsOf(scored.map((s) => ({ store: s.store, grade: s.breakdown.grade.quality!, biggest: null }))),
    },
  };
}
